/**
 * Legacy-Passwortfluss (OIDC_ENABLED=false, lokaler Entwicklungsmodus).
 * Im Cluster laeuft Keycloak, siehe authService.ts (PKCE). Der Typ `User`
 * und getCurrentUser() werden weiter genutzt; die Datei wird bewusst nicht
 * umbenannt, sonst kaemen per Upstream-Sync beide Namen zurueck
 * (Verbesserungsplan, Paket 7).
 */
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || '/api';

/**
 * Das Refresh-Token liegt als HttpOnly-Cookie (Path=/api/auth) beim Server
 * und ist fuer diesen Code unsichtbar. withCredentials, damit der Browser es
 * auch bei einer API auf anderem Origin (REACT_APP_API_URL) mitschickt bzw.
 * annimmt; X-Intermal-Auth ist der CSRF-Schutz fuer /refresh und /logout
 * (intermal/src/utils/refresh-cookie.ts).
 */
const MIT_COOKIE = { withCredentials: true };
const COOKIE_AUTH = { ...MIT_COOKIE, headers: { 'X-Intermal-Auth': '1' } };

export interface LoginCredentials {
  email: string;
  password: string;
}

export interface RegisterData {
  email: string;
  name: string;
  password: string;
}

export interface User {
  id: string;
  email: string;
  name: string;
  role: string;
  tenantId?: string;
  tenantRole?: string;
  /** Anwendungsrolle (mitarbeit, marketing, vertrieb, buchhaltung, inhaber), aus GET /api/auth/me. */
  appRolle?: string;
  /** Aufgeloeste Rechte der Person, aus GET /api/auth/me (siehe berechtigungen/). */
  rechte?: string[];
  /** Rechte je Person zusaetzlich zur Rolle (Benutzerverwaltung, GET /tenants/:id/users). */
  rechteZusatz?: string[];
  /** Rechte je Person, die der Rolle entzogen sind. */
  rechteEntzug?: string[];
  isActive?: boolean;
  lastLogin?: string;
  createdAt?: string;
}

export interface AuthResponse {
  user: User;
  accessToken: string;
}

class AuthService {
  /**
   * Login a user
   */
  async login(credentials: LoginCredentials): Promise<AuthResponse> {
    const response = await axios.post(`${API_URL}/auth/login`, credentials, MIT_COOKIE);
    return response.data.data;
  }

  /**
   * Register a new user
   */
  async register(data: RegisterData): Promise<AuthResponse> {
    const response = await axios.post(`${API_URL}/auth/register`, data, MIT_COOKIE);
    return response.data.data;
  }

  /**
   * Logout the current user: der Server entwertet das Refresh-Token aus dem
   * Cookie und loescht das Cookie.
   */
  async logout(): Promise<void> {
    await axios.post(`${API_URL}/auth/logout`, {}, COOKIE_AUTH);
  }

  /**
   * Refresh the access token (Refresh-Token kommt aus dem HttpOnly-Cookie
   * und wird dabei rotiert).
   */
  async refreshToken(): Promise<{ accessToken: string }> {
    const response = await axios.post(`${API_URL}/auth/refresh`, {}, COOKIE_AUTH);
    return response.data.data;
  }

  /**
   * Get current user info
   */
  async getCurrentUser(accessToken: string): Promise<User> {
    const response = await axios.get(`${API_URL}/auth/me`, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    });
    return response.data.data;
  }

  /**
   * Change password
   */
  async changePassword(
    accessToken: string,
    oldPassword: string,
    newPassword: string
  ): Promise<void> {
    await axios.put(
      `${API_URL}/auth/change-password`,
      { oldPassword, newPassword },
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      }
    );
  }
}

const authService = new AuthService();
export default authService;
